import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router';
import { Button, Card, Descriptions, Space, Tag, Typography, Skeleton, Alert } from 'antd';
import { ArrowLeftOutlined, PlayCircleOutlined, ClockCircleOutlined, DollarOutlined } from '@ant-design/icons';
import { getUroSDK } from '../../../services/URO';
import ScrollPanel from '../../../components/ScrollPanel';
import colors from '../../../utils/theme/colors';
import BeforeAfterPreview from '../../../components/BeforeAfterPreview';
import { FallbackIcon } from '../dashboardScreen/components/FallbackIcon';
import CodeExampleCard from '../../../components/apiDocs/CodeExampleCard';
import ResponseExampleCard from '../../../components/apiDocs/ResponseExampleCard';
import ApiEndpointBadge from '../../../components/apiDocs/ApiEndpointBadge';
import {
  generateModelCurl,
  generateModelAxios,
  generateModelPython,
  generateCheckJobCurl,
  generateCheckJobAxios,
  generateCheckJobPython,
} from '../../../utils/api/codeGenerators';
import { getModelRunResponses, getModelJobResponses } from '../../../utils/api/responseExamples';

const { Title, Text, Paragraph } = Typography;

function ModelDetailScreen() {
  const { modelId } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [model, setModel] = useState(null);
  const [imageError, setImageError] = useState(false);

  // Fetch model details
  useEffect(() => {
    const fetchModel = async () => {
      try {
        setLoading(true);
        setError(null);
        const { data } = await getUroSDK().getModelById(modelId);
        setModel(data);
      } catch (e) {
        setError(e.message || 'Failed to load model details');
      } finally {
        setLoading(false);
      }
    };

    if (modelId) {
      fetchModel();
    }
  }, [modelId]);

  const handleTryModel = () => {
    navigate(`/zen/model/${encodeURIComponent(modelId)}`);
  };

  const formatTypeName = (type) => {
    if (!type || typeof type !== 'string') return type || '';
    return type.replace(/_/g, ' ').split(' ').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  if (loading) {
    return (
      <ScrollPanel scrollable={false} style={{ height: 'auto' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px' }}>
          <Skeleton active avatar paragraph={{ rows: 8 }} />
        </div>
      </ScrollPanel>
    );
  }

  if (error) {
    return (
      <ScrollPanel scrollable={false} style={{ height: 'auto' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px' }}>
          <Alert
            message="Error Loading Model"
            description={error}
            type="error"
            showIcon
            action={
              <Button size="small" onClick={() => navigate('/explore')}>
                Back to Explore
              </Button>
            }
          />
        </div>
      </ScrollPanel>
    );
  }

  if (!model) {
    return (
      <ScrollPanel scrollable={false} style={{ height: 'auto' }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px' }}>
          <Alert
            message="Model Not Found"
            description="The requested model could not be found."
            type="warning"
            showIcon
            action={
              <Button size="small" onClick={() => navigate('/explore')}>
                Back to Explore
              </Button>
            }
          />
        </div>
      </ScrollPanel>
    );
  }

  return (
    <ScrollPanel scrollable={false} style={{ height: 'auto' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '20px' }}>
        {/* Header */}
        <div style={{ marginBottom: 24 }}>
          <Button 
            icon={<ArrowLeftOutlined />} 
            onClick={() => navigate('/explore')}
            style={{ marginBottom: 16 }}
          >
            Back to Explore
          </Button>
          
          <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
            {/* Model Image */}
            <div style={{ flexShrink: 0 }}>
              <Card 
                style={{ 
                  width: 300, 
                  background: colors.card,
                  border: `1px solid ${colors.border}`
                }}
                bodyStyle={{ padding: 0 }}
              >
                <div style={{ position: 'relative', overflow: 'hidden' }}>
                  {!imageError && model.model_cover_image_url && model.model_cover_2_image_url ? (
                    <BeforeAfterPreview
                      before={model.model_cover_image_url}
                      after={model.model_cover_2_image_url}
                      randomize={true}
                      style={{ 
                        width: '100%', 
                        aspectRatio: '1/1'
                      }}
                    />
                  ) : !imageError && model.model_cover_image_url ? (
                    <img
                      alt={model.model_name}
                      src={model.model_cover_image_url}
                      onError={() => setImageError(true)}
                      style={{ 
                        width: '100%', 
                        aspectRatio: '1/1', 
                        objectFit: 'cover', 
                        display: 'block'
                      }}
                    />
                  ) : (
                    <FallbackIcon 
                      type={model.model_type || 'default'} 
                      size={300} 
                      style={{ 
                        borderRadius: 0, 
                        aspectRatio: '1/1'
                      }} 
                    />
                  )}
                </div>
              </Card>
            </div>

            {/* Model Info */}
            <div style={{ flex: 1 }}>
              <Space direction="vertical" size={16} style={{ width: '100%' }}>
                <div>
                  <Title level={1} style={{ margin: 0, color: colors.foreground }}>
                    {model.model_name}
                  </Title>
                  {model.model_type && (
                    <Tag 
                      style={{ 
                        marginTop: 8,
                        borderRadius: 12,
                        border: 'none',
                        background: 'rgba(79, 70, 229, 0.1)',
                        color: '#4f46e5',
                        fontWeight: 500,
                        fontSize: 12,
                        padding: '4px 12px'
                      }}
                    >
                      {formatTypeName(model.model_type)}
                    </Tag>
                  )}
                </div>

                <Paragraph style={{ color: colors.muted_foreground, fontSize: 16 }}>
                  {model.model_description || 'Advanced AI model for creative tasks'}
                </Paragraph>

                {/* Quick Stats */}
                <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
                  {model.average_cost && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <DollarOutlined style={{ color: '#10b981' }} />
                      <Text style={{ color: colors.foreground }}>
                        <span style={{ fontSize: 12, opacity: 0.8 }}>~</span>
                        {Number(model.average_cost).toFixed(4)} URO per run
                      </Text>
                    </div>
                  )}
                  
                  {model.estimated_time && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <ClockCircleOutlined style={{ color: '#f59e0b' }} />
                      <Text style={{ color: colors.foreground }}>
                        ~{model.estimated_time}s runtime
                      </Text>
                    </div>
                  )}
                </div>

                {/* CTA Button */}
                <Button
                  type="primary"
                  size="large"
                  icon={<PlayCircleOutlined />}
                  onClick={handleTryModel}
                  style={{
                    background: 'linear-gradient(135deg, #4f46e5 0%, #06b6d4 100%)',
                    border: 'none',
                    borderRadius: 8,
                    height: 48,
                    fontSize: 16,
                    fontWeight: 600
                  }}
                >
                  Try This Model
                </Button>
              </Space>
            </div>
          </div>
        </div>

        {/* Detailed Information */}
        <Card 
          title="Model Details" 
          style={{ 
            background: colors.card,
            border: `1px solid ${colors.border}`
          }}
        >
          <Descriptions column={2} bordered>
            <Descriptions.Item label="Model ID">
              <Text code>{model.model_id}</Text>
            </Descriptions.Item>
            
            <Descriptions.Item label="Type">
              {formatTypeName(model.model_type)}
            </Descriptions.Item>
            
            {model.average_cost && (
              <Descriptions.Item label="Estimated Cost">
                <Text style={{ color: '#10b981', fontWeight: 600 }}>
                  <span style={{ fontSize: 12, opacity: 0.8 }}>~</span>
                  {Number(model.average_cost).toFixed(4)} URO per run
                </Text>
              </Descriptions.Item>
            )}
            
            {model.estimated_time && (
              <Descriptions.Item label="Estimated Runtime">
                <Text style={{ color: '#f59e0b', fontWeight: 600 }}>
                  ~{model.estimated_time} seconds
                </Text>
              </Descriptions.Item>
            )}
            
            {model.created_at && (
              <Descriptions.Item label="Created">
                {new Date(model.created_at).toLocaleDateString()}
              </Descriptions.Item>
            )}
            
            {model.updated_at && (
              <Descriptions.Item label="Last Updated">
                {new Date(model.updated_at).toLocaleDateString()}
              </Descriptions.Item>
            )}
          </Descriptions>
        </Card>

        {/* Parameters Section */}
        {model.parameters && model.parameters.length > 0 && (
          <Card
            title="Accepted Parameters"
            style={{
              marginTop: 24,
              background: colors.card,
              border: `1px solid ${colors.border}`
            }}
          >
            <div style={{ display: 'grid', gap: 16 }}>
              {model.parameters.map((param, index) => {
                const isRequired = Number(param.is_required) === 1 || param.is_required === true;
                let parsedAllowed = null;
                if (param.allowed_values) {
                  try {
                    parsedAllowed = typeof param.allowed_values === 'string'
                      ? JSON.parse(param.allowed_values)
                      : param.allowed_values;
                  } catch { parsedAllowed = null; }
                }
                return (
                  <div
                    key={index}
                    style={{
                      padding: 16,
                      background: colors.muted,
                      borderRadius: 8,
                      border: `1px solid ${colors.border}`
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <div>
                        <Text strong style={{ color: colors.foreground, fontSize: 15 }}>
                          {param.parameter_title}
                        </Text>
                        {param.parameter_name && (
                          <Text code style={{ marginLeft: 8, fontSize: 12, color: colors.muted_foreground }}>
                            {param.parameter_name}
                          </Text>
                        )}
                      </div>
                      <Tag color={isRequired ? 'red' : 'blue'}>
                        {isRequired ? 'Required' : 'Optional'}
                      </Tag>
                    </div>

                    <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: param.description ? 8 : 0 }}>
                      <Text style={{ color: colors.muted_foreground, fontSize: 12 }}>
                        Type: <Text code style={{ fontSize: 12 }}>{param.data_type}</Text>
                      </Text>
                      {param.default_value != null && param.default_value !== '' && (
                        <Text style={{ color: colors.muted_foreground, fontSize: 12 }}>
                          Default: <Text code style={{ fontSize: 12 }}>{String(param.default_value)}</Text>
                        </Text>
                      )}
                      {(param.allowed_min_value != null || param.allowed_max_value != null) && (
                        <Text style={{ color: colors.muted_foreground, fontSize: 12 }}>
                          Range: <Text code style={{ fontSize: 12 }}>
                            {param.allowed_min_value != null ? param.allowed_min_value : '∞'}
                            {' – '}
                            {param.allowed_max_value != null ? param.allowed_max_value : '∞'}
                          </Text>
                        </Text>
                      )}
                    </div>

                    {param.description && (
                      <Paragraph style={{ color: colors.muted_foreground, margin: '4px 0 0 0' }}>
                        {param.description}
                      </Paragraph>
                    )}

                    {parsedAllowed && Array.isArray(parsedAllowed) && parsedAllowed.length > 0 && (
                      <div style={{ marginTop: 8 }}>
                        <Text style={{ color: colors.muted_foreground, fontSize: 12, display: 'block', marginBottom: 4 }}>
                          Allowed values:
                        </Text>
                        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                          {parsedAllowed.map((val, i) => (
                            <Tag
                              key={i}
                              style={{
                                background: 'rgba(250, 139, 20, 0.1)',
                                border: '1px solid rgba(250, 139, 20, 0.3)',
                                color: '#FA8B14',
                                fontSize: 12,
                                borderRadius: 6,
                              }}
                            >
                              {String(val)}
                            </Tag>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* API Integration Section */}
        <div style={{ marginTop: 40 }}>
          <Title level={2} style={{ color: colors.foreground, marginBottom: 8 }}>
            API Integration
          </Title>
          <Paragraph style={{ color: colors.muted_foreground, marginBottom: 24 }}>
            Use the URO API to run this model programmatically. The code examples below are pre-filled with this model's ID and parameters.
          </Paragraph>

          <ApiEndpointBadge method="POST" path="/api/models/run-model" />

          <CodeExampleCard
            title="Step 1: Run This Model"
            description={`Send a POST request with model_id "${model.model_id}" and the required parameters. The API returns an image_job_id you can use to poll for results.`}
            codeExamples={{
              curl: generateModelCurl(model),
              axios: generateModelAxios(model),
              python: generateModelPython(model),
            }}
          />

          <ResponseExampleCard
            title="Run Model - Response Examples"
            responses={getModelRunResponses(model)}
          />

          <div style={{ marginTop: 32 }}>
            <ApiEndpointBadge method="GET" path="/api/image-jobs/image-job/{job_id}" />
          </div>

          <CodeExampleCard
            title="Step 2: Check Job Status"
            description="After submitting a job, poll this endpoint with the image_job_id from the response above. The job transitions from 'processing' to 'completed' or 'failed'."
            codeExamples={{
              curl: generateCheckJobCurl(),
              axios: generateCheckJobAxios(),
              python: generateCheckJobPython(),
            }}
          />

          <ResponseExampleCard
            title="Job Status - Response Example"
            responses={getModelJobResponses(model)}
          />
        </div>
      </div>
    </ScrollPanel>
  );
}

export default ModelDetailScreen;
